import { isTokenExpired, isTokenMatchingAddress } from "@/lib/utils"
import { CookieKey } from "@/configs/common"
import { useCallback, useEffect } from "react"
import { cookieStorage, useConnection } from "wagmi"
import { useCookies } from "react-cookie"

interface UseTokenValidationProps {
  setIsVerified: (verified: boolean) => void
}

const COOKIE_PATH = "/"

export const useTokenValidation = ({
  setIsVerified,
}: UseTokenValidationProps) => {
  const { address, isConnected } = useConnection()
  const [cookies, setCookie, removeCookie] = useCookies([CookieKey.AuthToken])

  const updateAccessToken = useCallback(
    (token?: string) => {
      if (token && !isTokenExpired(token)) {
        setCookie(CookieKey.AuthToken, token, { path: COOKIE_PATH })
        cookieStorage.setItem(CookieKey.AuthToken, token)
      } else {
        removeCookie(CookieKey.AuthToken, { path: COOKIE_PATH })
        cookieStorage.removeItem(CookieKey.AuthToken)
      }

      const newVerifiedState = !!token && !isTokenExpired(token)
      setIsVerified(newVerifiedState)
    },
    [setCookie, removeCookie, setIsVerified]
  )

  const accessToken = cookies[CookieKey.AuthToken] as string | undefined

  // Simple token validation - check if token matches current address
  useEffect(() => {
    if (!accessToken) {
      setIsVerified(false)
      return
    }

    if (!address) {
      setIsVerified(false)
      return
    }

    const tokenMatches = isTokenMatchingAddress(accessToken, address)
    const tokenNotExpired = !isTokenExpired(accessToken)

    if (tokenMatches && tokenNotExpired) {
      setIsVerified(true)
    } else {
      setIsVerified(false)
      if (!tokenMatches || !tokenNotExpired) {
        removeCookie(CookieKey.AuthToken, { path: COOKIE_PATH })
        cookieStorage.removeItem(CookieKey.AuthToken)
      }
    }
  }, [accessToken, address, isConnected, removeCookie, setIsVerified])

  // Handle focus events to check token validity when user switches tabs
  useEffect(() => {
    const handleFocus = () => {
      if (address && accessToken) {
        const tokenMatches = isTokenMatchingAddress(accessToken, address)
        const tokenNotExpired = !isTokenExpired(accessToken)

        if (!tokenMatches || !tokenNotExpired) {
          updateAccessToken(undefined)
          setIsVerified(false)
        }
      }
    }

    window.addEventListener("focus", handleFocus)
    return () => window.removeEventListener("focus", handleFocus)
  }, [address, accessToken, updateAccessToken, setIsVerified])

  return {
    accessToken: accessToken ?? undefined,
    updateAccessToken,
  }
}
